import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from './message.service';
import { SessionService } from './session.service';
import { UserAccountService } from '../../common/service/user-account.service';

/**
 * Handles the API rejecting the stored session - an expired or revoked token.
 * Every view keeps working against a session the server no longer accepts unless we sign the user
 * out here, so the http interceptor routes 401s from the api through this. Calls to services with
 * their own authentication (the global search) stay out of it: a 401 from those says nothing about
 * whether the user's own session is still valid.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiredHandler {
  constructor(
    private readonly _session: SessionService,
    private readonly _router: Router,
    private readonly _injector: Injector
  ) {}

  private _signingOut = false;

  /** Remembers where the user was and sends them to the login screen. Does nothing without a stored session. */
  handle(): void {
    if (this._signingOut || !this._session.hasSession) {
      return;
    }
    this._signingOut = true;

    const url = this._router.url;
    if (url && url !== '/' && !url.startsWith('/login')) {
      this._session.redirectUrl = url;
    }

    // Signing out completes UserAccountService.current, and doing that while the failing request is
    // still on its way up swallows the error its caller is waiting for - the login form only
    // re-enables itself when the login call fails. So let the failure be reported first.
    queueMicrotask(() => this.signOut());
  }

  private signOut(): void {
    // A later 401 is a new sign-out, so stop holding the door before deciding anything else.
    this._signingOut = false;

    if (!this._session.hasSession) {
      // Something else got there first while the failure was being reported - the account stream
      // signs out by itself when it cannot load the account.
      return;
    }

    // Resolved lazily: UserAccountService depends on HttpClient, which the interceptor using this
    // is part of building.
    const reachedLoginScreen = this._injector.get(UserAccountService).logout();

    reachedLoginScreen.then(
      reached => this.reportWhenStuck(reached),
      () => this.reportWhenStuck(false)
    );
  }

  private reportWhenStuck(reachedLoginScreen: boolean): void {
    if (reachedLoginScreen) {
      return;
    }

    // A pending changes guard can refuse to leave a dirty form, which cancels the navigation. The
    // session is gone either way, so say so rather than leaving the user on a page that can no
    // longer save. Their next navigation is caught by AuthGuard and ends on the login screen.
    this._injector.get(MessageService).showWarning('ERROR.SESSION_EXPIRED');
  }
}
